/* eslint-disable react/jsx-curly-brace-presence */
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { Headline, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { selectInputs, setUserInputs } from '../../redux/inputsReducer';
import { selectErrors, clearError, setError } from '../../redux/errorsReducer';
import { FEATURE_APP_DETAILS } from '../../constants';
import { convertToPromotionsType } from '../../functions/convertToPromotionsType';
import { selectDealerAndVehicleData } from '../../redux/dealerAndVehicleReducer';
import { selectForm } from '../../redux/formReducer';
import { digitalLeadsFormInput } from '../../utils/analyticsEventListeners';
import {
  CheckboxContainer,
  CcpaContaner,
  Legal,
  LegalContainer,
  LegalHeader,
  RegularDiscalimer,
  TextContainer,
  Url,
} from './LegalDisclaimer.styles';
import CheckBox from '../Inputs/CheckboxInput/CheckBox';
import { ErrorMessage } from '../Inputs/TextInput/TextInput.styles';
import CautionIcon from '../../assets/icons/CautionIcon';
import { selectAppConfig } from '../../redux/appConfigReducer';
import { getVehicleForPromotions } from '../../functions/getVehicleForPromotions';
import useSourceOfLead from '../../hooks/useSourceOfLead';
import { HOST_URL } from '../../constants/environment';
import { reversedPhoneNumberFormatter } from '../../functions/phoneNumberFormatter';

const components = {
  default: TextContainer,
  alphaText: Text,
};

const isAlpha = !process.env.IS_LEGACY;

const TextComponent = isAlpha ? components.alphaText : components.default;

const LegalDisclaimer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { vehicle, vehicleByCarline, vehicleByAudiCode, dealer } = useSelector(
    selectDealerAndVehicleData,
  );
  const contactForm = useSelector(selectForm);
  const dispatch = useDispatch();
  const {
    zipCode,
    ccpa,
    oemConsent,
    dealerConsent: dealerConsentInput,
    tcpaConsent: tcpaConsentInput,
    preferredContactMethod,
    phoneNumber,
    universalConsent,
  } = useSelector(selectInputs);
  const {
    offers,
    ccpaConsent,
    ccpaDisclaimer,
    dealerConsent,
    dealerConsentFr,
    market,
    tcpaConsent,
  } = useSelector(selectAppConfig);

  const isUSMarket = market?.toLowerCase().includes('us');

  const { requestSource } = useSourceOfLead();
  const promotion = convertToPromotionsType(offers!);
  const { universalConsent: consentErrorMessage, ccpa: ccpaErrorMessage } =
    useSelector(selectErrors);

  const isTier1Page = requestSource === 'tier_1';

  const phoneOrTextSelected = preferredContactMethod !== 'email';
  const shouldRenderTCPAConsent =
    !!tcpaConsent && phoneOrTextSelected && reversedPhoneNumberFormatter(phoneNumber).length === 10;

  const isCaliforniaZip = isUSMarket && Number(zipCode) >= 90001 && Number(zipCode) <= 96162;

  const privacyUrl =
    i18n.language === 'fr'
      ? 'https://www.audi.ca/fr/tools/navigation/layer/legal/privacy/#PersonalInformationWeCollect'
      : 'https://www.audi.ca/en/tools/navigation/layer/legal/privacy/#PersonalInformationWeCollect';

  const tier3PrivacyUrl = `${HOST_URL}/en/privacy/`;
  const tier3ContactUrl = `${HOST_URL}/en/contact-us/`;

  const handleChange = useCallback(
    (inputName: string, checked: boolean) => {
      const checkboxTouched = {
        inputTitle: inputName,
        inputValue: checked,
        action: 'select',
        inputType: 'checkbox',
      };

      if (!checked) {
        checkboxTouched.inputValue = checked;
        checkboxTouched.action = 'deselect';
      }

      // Use vehicle for VTP, vehicleByCarline/vehicleByAudiCode for NGC/MLP, or offers data for promotions
      const vehicleInView = vehicle || vehicleByCarline || vehicleByAudiCode;
      const vehicleData = vehicleInView || (offers ? getVehicleForPromotions(offers) : undefined);

      digitalLeadsFormInput(
        contactForm,
        vehicleData,
        FEATURE_APP_DETAILS,
        promotion,
        checkboxTouched,
      );

      dispatch(setUserInputs({ inputName, inputValue: checked }));
      if (!checked) {
        dispatch(setError({ field: inputName, error: t('digitalLeads.inputs.error.consent') }));
        return;
      }
      dispatch(clearError({ field: inputName }));
    },
    [contactForm, dispatch, offers, promotion, t, vehicle, vehicleByCarline, vehicleByAudiCode],
  );

  const parseDisclaimer = (disclaimer: string): JSX.Element[] | null => {
    if (!disclaimer) return null;

    const cleanedDisclaimer = disclaimer
      .replace(/&nbsp;/g, '') // Remove all &nbsp;
      .replace(/<\/?p>/g, '') // Remove all <p> tags
      .replace(/<br\s*\/?>/g, '\n') // Replace <br> with new line for splitting
      .replace(/<(?!a\s|\/a>)[^>]+>/g, '') // Remove all tags except <a>
      .trim();

    const paragraphs = cleanedDisclaimer.split(/\n\s*\n/).filter(Boolean);

    return paragraphs.map((paragraph, index) => {
      const parts = paragraph.split(/(<a[^>]*>.*?<\/a>)/g);

      return (
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
          key={`paragraph-${index + 1}`}
        >
          {parts.map((part, i) => {
            const linkMatch = part.match(/<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/);

            if (linkMatch) {
              const [, href, linkText] = linkMatch;
              if (isAlpha) {
                return (
                  <TextLink href={href} target="_blank" size="xs" variant="inline">
                    {linkText}
                  </TextLink>
                );
              }
              return (
                <Url key={`link-${i + 1}`} href={href} target="_blank">
                  {linkText}
                </Url>
              );
            }

            return <span key={`text-${i + 1}`}>{part}</span>; // Render plain text parts
          })}
        </TextComponent>
      );
    });
  };

  const renderTCPADisclaimer = useMemo(() => {
    if (!shouldRenderTCPAConsent) return null;
    return (
      <CheckboxContainer>
        <CheckBox
          inputName="tcpaConsent"
          data-testid="tcpaConsent-checkbox"
          onChange={handleChange}
          label={parseDisclaimer(tcpaConsent)}
          checked={tcpaConsentInput}
          isLegal
        />
      </CheckboxContainer>
    );
  }, [handleChange, shouldRenderTCPAConsent, tcpaConsent, tcpaConsentInput]);

  const renderAlphaCALegalDisclaimer = () => {
    if (!isTier1Page) {
      return (
        <>
          <TextComponent
            {...(isAlpha && {
              tag: 'p',
              variant: {
                type: 'copy',
                order: '3',
                style: 'normal',
              },
            })}
          >
            <Trans
              i18nKey={t('digitalLeads.inputs.dealerConsent')}
              components={{
                a1: isAlpha ? (
                  <TextLink href={tier3PrivacyUrl} target="_blank" size="xs" variant="inline">
                    {''}
                  </TextLink>
                ) : (
                  <Url href={tier3PrivacyUrl} target="_blank" />
                ),
                a2: isAlpha ? (
                  <TextLink href={tier3ContactUrl} target="_blank" size="xs" variant="inline">
                    {''}
                  </TextLink>
                ) : (
                  <Url href={tier3ContactUrl} target="_blank" />
                ),
              }}
            />
          </TextComponent>
          <div>
            <CheckBox
              inputName="dealerConsent"
              data-testid="dealerConsent-checkbox"
              onChange={handleChange}
              label={t('digitalLeads.inputs.dealerConsentCheck')}
              checked={dealerConsentInput}
              isLegal
            />
          </div>
          <TextComponent
            {...(isAlpha && {
              tag: 'p',
              variant: {
                type: 'copy',
                order: '3',
                style: 'normal',
              },
            })}
          >
            <Trans i18nKey={t('digitalLeads.inputs.oemConsent')} />
          </TextComponent>
          <div>
            <CheckBox
              inputName="oemConsent"
              data-testid="oemConsent-checkbox"
              onChange={handleChange}
              label={t('digitalLeads.inputs.oemConsentCheck')}
              checked={oemConsent}
              isLegal
            />
          </div>
        </>
      );
    }

    return (
      <>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          {t('digitalLeads.inputs.universalConsent')}
        </TextComponent>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          <Trans
            i18nKey="digitalLeads.inputs.universalConsentAgreement"
            components={{
              a1: isAlpha ? (
                <TextLink href={privacyUrl} target="_blank" size="xs" variant="inline">
                  {''}
                </TextLink>
              ) : (
                <Url href={privacyUrl} target="_blank" />
              ),
            }}
          />
        </TextComponent>
        <div>
          <CheckBox
            inputName="universalConsent"
            data-testid="universalConsent-checkbox"
            onChange={handleChange}
            label={t('digitalLeads.inputs.universalConsentCheck')}
            checked={universalConsent}
            isLegal
          />
        </div>
      </>
    );
  };

  const renderCALegalDisclaimer = () => {
    if (!isTier1Page) {
      return (
        <>
          <CheckboxContainer>
            <CheckBox
              inputName="dealerConsent"
              data-testid="dealerConsent-checkbox"
              onChange={handleChange}
              label={
                dealerConsent && dealerConsentFr
                  ? parseDisclaimer(i18n.language === 'fr' ? dealerConsentFr : dealerConsent)
                  : t('digitalLeads.inputs.dealerConsent')
              }
              transComponents={{
                a1: isAlpha ? (
                  <TextLink href={tier3PrivacyUrl} target="_blank" size="xs" variant="inline">
                    {''}
                  </TextLink>
                ) : (
                  <Url href={tier3PrivacyUrl} target="_blank" />
                ),
                a2: isAlpha ? (
                  <TextLink href={tier3ContactUrl} target="_blank" size="xs" variant="inline">
                    {''}
                  </TextLink>
                ) : (
                  <Url href={tier3ContactUrl} target="_blank" />
                ),
              }}
              checked={dealerConsentInput}
              isLegal
            />
          </CheckboxContainer>
          <CheckboxContainer>
            <CheckBox
              inputName="oemConsent"
              data-testid="oemConsent-checkbox"
              onChange={handleChange}
              label={t('digitalLeads.inputs.oemConsent')}
              checked={oemConsent}
              isLegal
            />
          </CheckboxContainer>
        </>
      );
    }

    return (
      <>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          {t('digitalLeads.inputs.universalConsent')}
        </TextComponent>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          <Trans
            i18nKey="digitalLeads.inputs.universalConsentAgreement"
            components={{
              a1: isAlpha ? (
                <TextLink href={privacyUrl} target="_blank" size="xs" variant="inline">
                  {''}
                </TextLink>
              ) : (
                <Url href={privacyUrl} target="_blank" />
              ),
            }}
          />
        </TextComponent>
      </>
    );
  };

  const handleCALegal = () => {
    return isAlpha ? renderAlphaCALegalDisclaimer() : renderCALegalDisclaimer();
  };

  const renderUSLegalDisclaimer = () => {
    if (!isTier1Page && dealerConsent) {
      return parseDisclaimer(dealerConsent);
    }
    return (
      <>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          <Trans
            i18nKey="digitalLeads.inputs.legalDisclaimerTerms"
            components={{
              a1: isAlpha ? (
                <TextLink
                  href="https://www.audiusa.com/us/web/en/compliance/terms-of-service.html"
                  target="_blank"
                  size="xs"
                  variant="inline"
                >
                  {''}
                </TextLink>
              ) : (
                <Url
                  href="https://www.audiusa.com/us/web/en/compliance/terms-of-service.html"
                  target="_blank"
                />
              ),
              a2: isAlpha ? (
                <TextLink
                  href="https://www.audiusa.com/us/web/en/compliance/privacy.html"
                  target="_blank"
                  size="xs"
                  variant="inline"
                >
                  {''}
                </TextLink>
              ) : (
                <Url
                  href="https://www.audiusa.com/us/web/en/compliance/privacy.html"
                  target="_blank"
                />
              ),
            }}
          />
        </TextComponent>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          {dealer
            ? t('digitalLeads.inputs.legalDisclaimerDealer1')
            : t('digitalLeads.inputs.legalDisclaimerNonDealer1')}
        </TextComponent>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          {dealer
            ? t('digitalLeads.inputs.legalDisclaimerDealer2')
            : t('digitalLeads.inputs.legalDisclaimerNonDealer2')}
        </TextComponent>
        {isAlpha && (
          <div>
            <CheckBox
              inputName="universalConsent"
              onChange={handleChange}
              label={t('digitalLeads.inputs.universalConsentCheck')}
              checked={universalConsent}
              isLegal
            />
          </div>
        )}
      </>
    );
  };

  const renderCcpaDisclaimer = () => {
    if (ccpaDisclaimer) return parseDisclaimer(ccpaDisclaimer);

    return (
      <>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          <Trans i18nKey="digitalLeads.inputs.ccpaDisclaimer1" />
        </TextComponent>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          <Trans i18nKey="digitalLeads.inputs.ccpaDisclaimer2" />
        </TextComponent>
        <TextComponent
          {...(isAlpha && {
            tag: 'p',
            variant: {
              type: 'copy',
              order: '3',
              style: 'normal',
            },
          })}
        >
          <Trans i18nKey="digitalLeads.inputs.ccpaDisclaimer3" />
        </TextComponent>
      </>
    );
  };

  return (
    <Legal data-testid="dl-legal-disclaimer">
      {renderTCPADisclaimer}
      {isCaliforniaZip && (
        <CcpaContaner>
          {isAlpha ? (
            <Headline
              margin={['0', '0', '16px', '0']}
              variant={{ type: 'headline', order: '4', weight: 'normal' }}
              tag="h3"
            >
              {t('digitalLeads.inputs.ccpaLabel')}
            </Headline>
          ) : (
            <LegalHeader>{t('digitalLeads.inputs.ccpaLabel')}</LegalHeader>
          )}

          <LegalContainer>{renderCcpaDisclaimer()}</LegalContainer>
          <CheckboxContainer>
            <CheckBox
              inputName="ccpa"
              data-testid="ccpa-checkbox"
              onChange={handleChange}
              label={ccpaConsent || t('digitalLeads.inputs.ccpaConsent')}
              checked={ccpa}
              isLegal
            />
          </CheckboxContainer>
          {!!ccpaErrorMessage && (
            <ErrorMessage data-testid="dl-legal-disclaimer-error-message">
              <CautionIcon />
              {ccpaErrorMessage}
            </ErrorMessage>
          )}
        </CcpaContaner>
      )}
      <RegularDiscalimer>
        {isUSMarket ? renderUSLegalDisclaimer() : handleCALegal()}
        {!!consentErrorMessage && (
          <ErrorMessage data-testid="dl-legal-disclaimer-error-message">
            <CautionIcon />
            {consentErrorMessage}
          </ErrorMessage>
        )}
      </RegularDiscalimer>
    </Legal>
  );
};

export default LegalDisclaimer;
